/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AlertTriangle,
  Check,
  HelpCircle,
  Info,
  X,
  ArrowRight,
  ArrowDown,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plan, PlanCategory } from "@/pages/billing/pricing-data";
import { PaymentInterval } from "@/types/subscription.types";

// Create a completely flexible type without inheriting Plan constraints
export interface PlanForComparison {
  name?: string;
  billingCycle?: PaymentInterval;
  type?: PlanCategory.B2B | PlanCategory.B2C | "SEED_LIST";
  category?: PlanCategory.B2B | PlanCategory.B2C | "SEED_LIST";
  features: {
    warmupEmails?: number;
    healthTests?: number;
    inboxPlacementTests?: number;
    seedAccounts?: number;
    senderProfiles?: number;
    support: string;
    api: boolean;
  };
  price: number;

  startDate?: string;
  nextBillingDate?: string;
  usage?: {
    warmupEmails?: number;
    healthTests?: number;
    inboxPlacementTests?: number;
    seedAccounts?: number;
    senderProfiles?: number;
    apiCalls?: number;
    activeMailboxes?: number;
  };
}

interface PlanComparisonTableProps {
  currentPlan: PlanForComparison | null;
  newPlan?: PlanForComparison | null;
  usage: any;
  impactData?: {
    warmupEmailsReduction: number;
    healthTestsReduction: number;
    supportChange: boolean;
    apiAccessChange: boolean;
  };
}

// Helper function to extract numeric values from feature strings
const extractFeatureValue = (features: any, keyword: string): number => {
  // Check if features is not an array - might be an object with direct property access
  if (!Array.isArray(features)) {
    // If it's an object with direct properties like warmupEmails, healthTests, etc.
    if (typeof features === "object" && features !== null) {
      // Try to extract from specific properties based on keyword
      if (
        keyword.toLowerCase().includes("warmup") &&
        features.warmupEmails !== undefined
      ) {
        return Number(features.warmupEmails);
      }
      if (
        keyword.toLowerCase().includes("health test") &&
        features.healthTests !== undefined
      ) {
        return Number(features.healthTests);
      }
      if (
        keyword.toLowerCase().includes("inbox placement") &&
        features.inboxPlacementTests !== undefined
      ) {
        return Number(features.inboxPlacementTests);
      }
    }
    return 0;
  }

  // Original array-based logic
  const feature = features.find((f) =>
    f.toLowerCase().includes(keyword.toLowerCase())
  );
  if (!feature) return 0;
  const match = feature.match(/(\d+,\d+|\d+)/);
  if (!match) return 0;
  return parseInt(match[0].replace(",", ""), 10);
};

// Helper to get support level from features
const getSupportLevel = (features: any): string => {
  // If features is an object with a support property
  if (
    !Array.isArray(features) &&
    typeof features === "object" &&
    features !== null
  ) {
    if (features.support) return features.support;
    return "Email Support";
  }

  // Original array-based logic
  if (!Array.isArray(features)) return "Email Support";
  if (features.some((f) => f.includes("Dedicated Account Manager")))
    return "Dedicated Manager";
  if (features.some((f) => f.includes("Live Chat Support"))) return "Live Chat";
  return "Email Support";
};

// Helper to check if API access is included
const hasApiAccess = (features: any): boolean => {
  // If features is an object with an api property
  if (
    !Array.isArray(features) &&
    typeof features === "object" &&
    features !== null
  ) {
    return !!features.api;
  }

  // Original array-based logic
  if (!Array.isArray(features)) return false;
  return features.some((f) => f.includes("API Access"));
};

// Helper function to get price display with safe null checking
const getPriceDisplay = (plan?: PlanForComparison): string => {
  if (!plan || !plan.price) return "0";

  // Handle string price
  if (typeof plan.price === "string") return plan.price;

  // Handle number price
  if (typeof plan.price === "number") return plan.price.toString();

  // Default case
  return "0";
};

// Add default empty plan for null/undefined case
const emptyPlan: PlanForComparison = {
  name: "No Plan",
  features: {
    warmupEmails: 0,
    healthTests: 0,
    inboxPlacementTests: 0,
    support: "Email Support",
    api: false,
  },
  price: 0,
};

interface ComparisonRowProps {
  title: string;
  tooltipText: string;
  currentValue: string | number;
  newValue: string | number;
  currentUsage?: string | number;
  impactText: string;
  impactSubtext: string;
  impactType: 'red' | 'amber' | 'green';
  impactIcon: React.ReactNode;
}

function ComparisonRow({
  title,
  tooltipText,
  currentValue,
  newValue,
  currentUsage,
  impactText,
  impactSubtext,
  impactType,
  impactIcon,
}: ComparisonRowProps) {
  const getImpactStyles = (type: 'red' | 'amber' | 'green') => {
    switch (type) {
      case 'red':
        return 'bg-red-50 text-red-700';
      case 'amber':
        return 'bg-amber-50 text-amber-700';
      case 'green':
        return 'bg-green-50 text-green-700';
    }
  };

  return (
    <div className="px-4 sm:px-6 py-4 border-t first:border-t-0">
      {/* Mobile Layout */}
      <div className="block lg:hidden">
        <div className="flex items-center gap-1 mb-3">
          <span className="font-medium">{title}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-sm">Current</div>
              {currentUsage && (
                <div className="text-xs text-gray-500">
                  Usage: {currentUsage}
                </div>
              )}
            </div>
            <div className="font-medium">{currentValue}</div>
          </div>
          <div className="flex justify-center">
            <ArrowDown className="h-5 w-5 text-red-500" />
          </div>
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <div className="font-medium text-sm">New</div>
            <div className="font-medium">{newValue}</div>
          </div>
          <div className={`flex items-center gap-2 ${getImpactStyles(impactType)} py-2 px-3 rounded-lg`}>
            {impactIcon}
            <div>
              <span className="font-medium text-sm">{impactText}</span>
              <div className="text-xs">{impactSubtext}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:grid lg:grid-cols-12 lg:items-center">
        <div className="col-span-3 flex items-center gap-1">
          <span className="font-medium">{title}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="col-span-3 text-center py-2 px-3">
          <div className="font-medium">{currentValue}</div>
          {currentUsage && (
            <div className="text-xs text-gray-500">
              Current usage: {currentUsage}
            </div>
          )}
        </div>
        <div className="flex justify-center items-center">
          <ArrowRight className="h-5 w-5 text-red-500" />
        </div>
        <div className="col-span-2 text-center">
          <div className="font-medium">{newValue}</div>
        </div>
        <div className="col-span-3">
          <div className={`flex items-center gap-2 ${getImpactStyles(impactType)} py-2 px-3 rounded-lg`}>
            {impactIcon}
            <div>
              <span className="font-medium">{impactText}</span>
              <div className="text-xs">{impactSubtext}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlanComparisonTable({
  currentPlan,
  newPlan = emptyPlan,
  usage,
  impactData,
}: PlanComparisonTableProps) {
  // Use the provided newPlan or fall back to emptyPlan (already handled by default param)
  if (!currentPlan || !newPlan) return null;
  
  // Check if this is a seed-list plan
  const isSeedListPlan = 
    (currentPlan.type === "SEED_LIST" || currentPlan.category === "SEED_LIST") ||
    (currentPlan.features?.seedAccounts !== undefined);
  
  // Extract feature values for seed-list plans
  const currentSeedAccounts = isSeedListPlan 
    ? (currentPlan.features?.seedAccounts || 0)
    : 0;
  const newSeedAccounts = isSeedListPlan 
    ? (newPlan.features?.seedAccounts || 0)
    : 0;
  
  const currentSenderProfiles = isSeedListPlan 
    ? (currentPlan.features?.senderProfiles || 0)
    : 0;
  const newSenderProfiles = isSeedListPlan 
    ? (newPlan.features?.senderProfiles || 0)
    : 0;

  // Extract feature values for B2B/B2C plans
  const currentWarmupEmailsDaily = extractFeatureValue(
    currentPlan.features,
    "Warmup Emails Send Per Day"
  );
  const newWarmupEmailsDaily = extractFeatureValue(
    newPlan.features,
    "Warmup Emails Send Per Day"
  );

  const currentHealthTests = extractFeatureValue(
    currentPlan.features,
    "Email Health Test Per Month"
  );
  const newHealthTests = extractFeatureValue(
    newPlan.features,
    "Email Health Test Per Month"
  );

  const currentInboxPlacement = extractFeatureValue(
    currentPlan.features,
    "Inbox Placement Test Per Month"
  );
  const newInboxPlacement = extractFeatureValue(
    newPlan.features,
    "Inbox Placement Test Per Month"
  );

  const currentSupport = getSupportLevel(currentPlan.features);
  const newSupport = getSupportLevel(newPlan.features);

  const currentHasApiAccess = hasApiAccess(currentPlan.features);
  const newHasApiAccess = hasApiAccess(newPlan.features);

  const supportChange = currentSupport !== newSupport;
  const apiAccessChange = currentHasApiAccess && !newHasApiAccess;

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      {/* Price comparison header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 sm:p-6 border-b gap-4">
        <div>
          <h3 className="text-lg font-semibold">Monthly Price</h3>
          <p className="text-sm text-gray-500">Per mailbox</p>
        </div>
        <div className="flex items-center justify-center sm:gap-12 gap-6">
          <div className="text-center">
            <div className="text-lg font-bold">
              ${getPriceDisplay(currentPlan)}
            </div>
            <div className="text-sm text-gray-500">Current</div>
          </div>
          <div className="text-gray-400">
            <ArrowRight className="h-5 w-5" />
          </div>
          <div className="text-center">
            <div className="text-lg font-bold">${getPriceDisplay(newPlan)}</div>
            <div className="text-sm text-gray-500">New</div>
          </div>
        </div>
      </div>

      {/* Feature comparison rows */}
      <div className="mt-0">
        {isSeedListPlan ? (
          <>
            {/* Seed Accounts Row */}
            <ComparisonRow
              title="Seed Accounts"
              tooltipText="Number of seed accounts available for email warmup"
              currentValue={currentSeedAccounts?.toLocaleString()}
              newValue={newSeedAccounts?.toLocaleString()}
              currentUsage={usage.seedAccounts?.toLocaleString()}
              impactText={`${currentSeedAccounts - newSeedAccounts} fewer seed accounts`}
              impactSubtext="High impact on service"
              impactType="red"
              impactIcon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}
            />

            {/* Sender Profiles Row */}
            <ComparisonRow
              title="Sender Profiles"
              tooltipText="Number of sender profiles available"
              currentValue={`${currentSenderProfiles?.toLocaleString()} profile${currentSenderProfiles !== 1 ? "s" : ""}`}
              newValue={`${newSenderProfiles?.toLocaleString()} profile${newSenderProfiles !== 1 ? "s" : ""}`}
              currentUsage={usage.senderProfiles?.toLocaleString()}
              impactText={`${currentSenderProfiles - newSenderProfiles} fewer sender profile${currentSenderProfiles - newSenderProfiles !== 1 ? "s" : ""}`}
              impactSubtext="High impact on service"
              impactType="red"
              impactIcon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}
            />
          </>
        ) : (
          <>
            {/* Warmup Emails Row */}
            <ComparisonRow
              title="Warmup Emails"
              tooltipText="Daily emails sent to warm up your email accounts"
              currentValue={`${currentWarmupEmailsDaily?.toLocaleString()} per day`}
              newValue={`${newWarmupEmailsDaily?.toLocaleString()} per day`}
              currentUsage={usage.warmupEmails?.toLocaleString()}
              impactText={`${impactData?.warmupEmailsReduction || currentWarmupEmailsDaily - newWarmupEmailsDaily} fewer emails`}
              impactSubtext="High impact on service"
              impactType="red"
              impactIcon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}
            />

            {/* Health Tests Row */}
            <ComparisonRow
              title="Email Health Tests"
              tooltipText="Tests to check email health and inbox placement"
              currentValue={`${currentHealthTests?.toLocaleString()} per month`}
              newValue={`${newHealthTests?.toLocaleString()} per month`}
              currentUsage={usage.healthTests?.toLocaleString()}
              impactText={`${impactData?.healthTestsReduction || currentHealthTests - newHealthTests} fewer tests`}
              impactSubtext="High impact on service"
              impactType="red"
              impactIcon={<AlertTriangle className="h-4 w-4 flex-shrink-0" />}
            />

            {/* Inbox Placement Row */}
            <ComparisonRow
              title="Inbox Placement Tests"
              tooltipText="Email delivery directly to inbox instead of spam folder"
              currentValue={`${currentInboxPlacement?.toLocaleString()} per month`}
              newValue={`${newInboxPlacement?.toLocaleString()} per month`}
              impactText={`${currentInboxPlacement - newInboxPlacement} fewer tests`}
              impactSubtext="High impact on deliverability"
              impactType="red"
              impactIcon={<X className="h-4 w-4 flex-shrink-0" />}
            />
          </>
        )}

        {/* Support Level Row */}
        <ComparisonRow
          title="Support Level"
          tooltipText="Level of customer support provided"
          currentValue={currentSupport}
          newValue={newSupport}
          impactText={supportChange ? "Support level reduced" : "No change"}
          impactSubtext=""
          impactType={supportChange ? "amber" : "green"}
          impactIcon={supportChange ? <Info className="h-4 w-4 flex-shrink-0" /> : <Check className="h-4 w-4 flex-shrink-0" />}
        />

        {/* API Access Row - Only show for B2B/B2C plans */}
        {!isSeedListPlan && (currentHasApiAccess || newHasApiAccess) && (
          <ComparisonRow
            title="API Access"
            tooltipText="Access to our API for integration with your tools"
            currentValue={currentHasApiAccess ? "Yes" : "No"}
            newValue={newHasApiAccess ? "Yes" : "No"}
            impactText={apiAccessChange ? "API access removed" : "No change"}
            impactSubtext=""
            impactType={apiAccessChange ? "amber" : "green"}
            impactIcon={apiAccessChange ? <AlertTriangle className="h-4 w-4 flex-shrink-0" /> : <Check className="h-4 w-4 flex-shrink-0" />}
          />
        )}
      </div>
    </div>
  );
}
